import React from "react";

import {
  ResponsiveContainer,
  BarChart,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  Bar,
  LabelList,
} from "recharts";

import {
  Card,
  CardContent,
  Typography,
} from "@material-ui/core";

import { formatCompact } from "../utils";

interface DistributionRow {
  fieldName: string;

  Added: number;
  Modified: number;
  Deleted: number;

  AddedValue: number;
  ModifiedValue: number;
  DeletedValue: number;

  TotalValue?: number;
}

interface Props {
  data: DistributionRow[];
}

const CustomTooltip = ({
  active,
  payload,
}: any) => {

  if (!active || !payload?.length)
    return null;

  const row = payload[0].payload;

  return (

    <div
      style={{
        background: "#fff",
        border: "1px solid #E5E7EB",
        borderRadius: 10,
        padding: 14,
        boxShadow: "0 6px 18px rgba(0,0,0,.15)",
        minWidth: 220,
      }}
    >

      <Typography
        style={{
          fontWeight: 700,
          marginBottom: 10,
        }}
      >
        {row.fieldName}
      </Typography>

      <div
        style={{
          color: "#22C55E",
          marginBottom: 6,
        }}
      >
        <strong>Added</strong>

        <br />

        {row.Added.toFixed(2)}%

        {" • "}

        {formatCompact(row.AddedValue)}
      </div>

      <div
        style={{
          color: "#3B82F6",
          marginBottom: 6,
        }}
      >
        <strong>Modified</strong>

        <br />

        {row.Modified.toFixed(2)}%

        {" • "}

        {formatCompact(row.ModifiedValue)}
      </div>

      <div
        style={{
          color: "#EF4444",
          marginBottom: 8,
        }}
      >
        <strong>Deleted</strong>

        <br />

        {row.Deleted.toFixed(2)}%

        {" • "}

        {formatCompact(row.DeletedValue)}
      </div>

      <hr />

      <div
        style={{
          marginTop: 8,
          fontWeight: 700,
        }}
      >
        Total : {formatCompact(row.TotalValue)}
      </div>

    </div>

  );
};

const DistributionChart = ({
  data,
}: Props) => {

  const sorted = [...data].sort(
    (a, b) =>
      b.TotalValue - a.TotalValue
  );

  return (

    <Card
      elevation={3}
      style={{
        borderRadius: 16,
      }}
    >

      <CardContent>

        <Typography
          variant="h6"
          style={{
            fontWeight: 700,
            marginBottom: 20,
          }}
        >
          Action Distribution by Field
        </Typography>

        <ResponsiveContainer
          width="100%"
          height={Math.max(480, sorted.length * 38)}
        >

          <BarChart
            data={sorted}
            layout="vertical"
            barCategoryGap="38%"
            barGap={3}
            margin={{
              top: 10,
              right: 35,
              left: 40,
              bottom: 10,
            }}
          >

            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
            />

            <XAxis
              type="number"
              domain={[0, 100]}
              tickFormatter={(v) =>
                `${Number(v).toFixed(0)}%`
              }
            />

            <YAxis
              type="category"
              width={220}
              dataKey="fieldName"
              tick={{
                fontSize: 12,
                fontWeight: 600,
              }}
              tickFormatter={(value) =>
                value.length > 28
                  ? value.substring(0, 28) + "..."
                  : value
              }
            />

            <Tooltip
              content={<CustomTooltip />}
            />

            <Legend
              verticalAlign="top"
              height={40}
              wrapperStyle={{
                fontWeight: 600,
              }}
            />

            <Bar
              dataKey="Added"
              stackId="a"
              fill="#22C55E"
              radius={[0, 0, 0, 0]}
              barSize={24}
            >
              <LabelList
                dataKey="Added"
                position="inside"
                formatter={(v: number) =>
                  v > 7
                    ? `${v.toFixed(1)}%`
                    : ""
                }
                fill="#fff"
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                }}
              />
            </Bar>

            <Bar
              dataKey="Modified"
              stackId="a"
              fill="#3B82F6"
              barSize={24}
            >
              <LabelList
                dataKey="Modified"
                position="inside"
                formatter={(v: number) =>
                  v > 7
                    ? `${v.toFixed(1)}%`
                    : ""
                }
                fill="#fff"
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                }}
              />
            </Bar>

            <Bar
              dataKey="Deleted"
              stackId="a"
              fill="#EF4444"
              radius={[0, 8, 8, 0]}
              barSize={24}
            >
              <LabelList
                dataKey="Deleted"
                position="inside"
                formatter={(v: number) =>
                  v > 7
                    ? `${v.toFixed(1)}%`
                    : ""
                }
                fill="#fff"
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                }}
              />
            </Bar>

          </BarChart>

        </ResponsiveContainer>

      </CardContent>

    </Card>

  );
};

export default DistributionChart;